import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useLanguages } from "@/hooks/useLanguages";
import { Settings, Globe, Key, TestTube } from "lucide-react";

interface TranslationSettings {
  id: string;
  libretranslate_api_url: string;
  libretranslate_api_key: string | null;
  auto_translate: boolean;
  show_language_selector: boolean;
  default_language: string;
  fallback_to_api: boolean;
  created_at: string;
  updated_at: string;
}

const AdminTranslationSettings = () => {
  const [settings, setSettings] = useState<TranslationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formData, setFormData] = useState({
    libretranslate_api_url: "https://libretranslate.com",
    libretranslate_api_key: "",
    auto_translate: false,
    show_language_selector: true,
    default_language: "es",
    fallback_to_api: true,
  });

  const { languages } = useLanguages();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchSettings();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin/login');
      return;
    }

    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('*')
      .eq('user_id', session.user.id)
      .single();

    if (!adminUser) {
      navigate('/admin/login');
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('translation_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching translation settings:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar las configuraciones",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setSettings(data);
        setFormData({
          libretranslate_api_url: data.libretranslate_api_url,
          libretranslate_api_key: data.libretranslate_api_key || "",
          auto_translate: data.auto_translate,
          show_language_selector: data.show_language_selector,
          default_language: data.default_language,
          fallback_to_api: data.fallback_to_api,
        });
      }
    } catch (error) {
      console.error('Error fetching translation settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las configuraciones",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        ...formData,
        libretranslate_api_key: formData.libretranslate_api_key || null,
      };

      // Update translation settings
      let error;
      if (settings) {
        const { error: updateError } = await supabase
          .from('translation_settings')
          .update(updateData)
          .eq('id', settings.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('translation_settings')
          .insert([updateData]);
        error = insertError;
      }

      if (error) {
        console.error('Error saving translation settings:', error);
        toast({
          title: "Error",
          description: "No se pudieron guardar las configuraciones",
          variant: "destructive",
        });
        return;
      }

      // Sync default language with languages table
      await supabase
        .from('languages')
        .update({ is_default: false })
        .neq('code', formData.default_language);
      
      await supabase
        .from('languages')
        .update({ is_default: true })
        .eq('code', formData.default_language);

      toast({
        title: "Éxito",
        description: "Configuraciones guardadas exitosamente",
      });

      fetchSettings();
    } catch (error) {
      console.error('Error saving translation settings:', error);
      toast({
        title: "Error",
        description: "No se pudieron guardar las configuraciones",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const testTranslationAPI = async () => {
    setTesting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('translate-text', {
        body: {
          text: 'Hola mundo',
          source: 'es',
          target: 'en'
        }
      });

      if (error) {
        console.error('Translation test error:', error);
        toast({
          title: "Error en la prueba",
          description: "No se pudo conectar con el servicio de traducción",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Prueba exitosa",
        description: `Traducción: "Hola mundo" → "${data.translatedText}"`,
      });
    } catch (error) {
      console.error('Translation test error:', error);
      toast({
        title: "Error en la prueba",
        description: "No se pudo probar el servicio de traducción",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  const syncDefaultLanguages = async () => {
    try {
      // Sync translation settings with languages table
      if (settings) {
        // Update all languages to not be default
        await supabase
          .from('languages')
          .update({ is_default: false })
          .neq('code', '');
        
        // Set the language from settings as default
        await supabase
          .from('languages')
          .update({ is_default: true })
          .eq('code', settings.default_language);
        
        toast({
          title: "Sincronización exitosa",
          description: "Los idiomas por defecto han sido sincronizados",
        });
        
        // Refresh data
        fetchSettings();
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast({
        title: "Error en sincronización",
        description: "No se pudo sincronizar los idiomas",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">Configuración de Traducciones</h1>
          <div className="flex gap-2">
            <Button
              onClick={syncDefaultLanguages}
              variant="outline"
            >
              <Globe className="h-4 w-4 mr-2" />
              Sincronizar
            </Button>
            <Button
              onClick={testTranslationAPI}
              disabled={testing}
              variant="outline"
            >
              <TestTube className="h-4 w-4 mr-2" />
              {testing ? "Probando..." : "Probar API"}
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* API Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Configuración de LibreTranslate API
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api_url">URL del API</Label>
                <Input
                  id="api_url"
                  value={formData.libretranslate_api_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, libretranslate_api_url: e.target.value }))}
                  placeholder="https://libretranslate.com"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  URL del servicio LibreTranslate. Puedes usar el servicio público o tu propia instancia.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="api_key">API Key (opcional)</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={formData.libretranslate_api_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, libretranslate_api_key: e.target.value }))}
                  placeholder="Tu API key de LibreTranslate"
                />
                <p className="text-sm text-muted-foreground">
                  API key para acceso a LibreTranslate. Opcional para el servicio público, pero recomendado para mayor estabilidad.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Configuración General
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="default_language">Idioma por Defecto</Label>
                <Select
                  value={formData.default_language}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, default_language: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((language) => (
                      <SelectItem key={language.code} value={language.code}>
                        {language.flag_emoji} {language.native_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Idioma principal del sitio web.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Mostrar Selector de Idioma</Label>
                    <p className="text-sm text-muted-foreground">
                      Muestra el selector de idioma en el sitio web
                    </p>
                  </div>
                  <Switch
                    checked={formData.show_language_selector}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_language_selector: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Traducción Automática</Label>
                    <p className="text-sm text-muted-foreground">
                      Traduce automáticamente el contenido cuando el usuario cambia de idioma
                    </p>
                  </div>
                  <Switch
                    checked={formData.auto_translate}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, auto_translate: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Fallback a API</Label>
                    <p className="text-sm text-muted-foreground">
                      Usar LibreTranslate cuando no haya traducción personalizada disponible
                    </p>
                  </div>
                  <Switch
                    checked={formData.fallback_to_api}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, fallback_to_api: checked }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Información del Sistema
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  <strong>Diccionario Personalizado:</strong> Las traducciones personalizadas tienen prioridad sobre la API.
                </p>
                <p>
                  <strong>LibreTranslate:</strong> Servicio de traducción automática de código abierto.
                </p>
                <p>
                  <strong>Fallback:</strong> Si no hay traducción personalizada y el fallback está activo, se usa la API.
                </p>
                <p>
                  <strong>Rendimiento:</strong> Las traducciones personalizadas son más rápidas que las de API.
                </p>
                <p>
                  <strong>Sincronizar:</strong> Use el botón sincronizar si ve discrepancias entre los idiomas por defecto.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving ? "Guardando..." : "Guardar Configuración"}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminTranslationSettings;